import { useEffect, useState, type ReactNode } from "react";
import { api, sentListing, type Listing } from "../api";
import { openCapsule, revealWithKey, type CapsuleView } from "../capsules";
import { isKeyRequestCancelled, useSession } from "../session";
import { fingerprint } from "../keys";
import { Attachments } from "./Attachments";
import { downloadCapsule, downloadableEvent } from "../download";
import { Chest } from "../Chest";
import { formatCountdown, formatCountdownShort, formatTime, formatTimeShort, useNow } from "../time";
import { errorText, useT } from "../i18n";
import { Icon } from "../Icon";
import type { MessageScope } from "../PeoplePanel";
import { initials, PersonMenu } from "../PersonMenu";
import { Menu } from "../Menu";
import { navigate } from "../router";
import { creationTime } from "../messageFilters";

export function DamagedRow({ reason }: { reason: string }) {
  const t = useT();
  return (
    <li className="capsule damaged">
      <p className="error small">{t.capsules.damaged(reason)}</p>
    </li>
  );
}

/**
 * Who wrote it (PLAN.md 4.10): the name signed into the capsule at sending time, the pubkey
 * fingerprint (the identity itself), and today's name when the directory now shows another one.
 */
function Author({ view, detailed = false, hidden = false }: { view: CapsuleView; detailed?: boolean; hidden?: boolean }) {
  const t = useT();
  const { me, memberByKey } = useSession();
  if (hidden) return <>{t.capsules.discreetSender}</>;
  if (view.unverified) {
    return (
      <span className="warn-text" title={t.capsules.unverifiedNote}>
        {t.capsules.unverifiedSender}
        {detailed && view.author && <> <code>{fingerprint(view.author)}</code> <span className="muted">{t.capsules.unverifiedNote}</span></>}
      </span>
    );
  }
  if (!view.author) return <>{t.capsules.fromUnknown}</>;
  if (view.author === me.pubkey) return <>{detailed ? t.capsules.fromYou : t.common.you}</>;
  const current = memberByKey(view.author)?.display_name;
  const signed = view.sender?.display_name;
  return (
    <>
      {detailed ? t.capsules.from(signed ?? current ?? t.common.unknown) : signed ?? current ?? t.common.unknown}
      {detailed && <>{view.sender?.name && <span> @{view.sender.name}</span>} <code>{fingerprint(view.author)}</code></>}
      {signed && current && current !== signed && <span>{t.capsules.now(current)}</span>}
      {detailed && !signed && <span>{t.capsules.noName}</span>}
    </>
  );
}

/**
 * CC (PLAN.md 4.21): who else received the capsule, as the sender listed them inside the seal (the
 * name they used, today's name when it differs, the key's fingerprint). The list is the sender's claim.
 */
function CcLine({ cc }: { cc: NonNullable<CapsuleView["cc"]> }) {
  const t = useT();
  const { me, memberByKey } = useSession();
  const name = (n: { pubkey: string; display_name: string }) => {
    const current = n.pubkey === me.pubkey ? t.common.you : memberByKey(n.pubkey)?.display_name;
    return <span key={n.pubkey} className="cc-name">{n.display_name}{current && current !== n.display_name ? <span className="muted">{t.capsules.now(current)}</span> : null} <code>{fingerprint(n.pubkey)}</code></span>;
  };
  return (
    <p className="small muted cc-line">
      {cc.to.length > 0 && <>{t.envelopes.toLabel} {cc.to.map(name)}</>}
      {cc.to.length > 0 && cc.cc.length > 0 && " · "}
      {cc.cc.length > 0 && <>{t.envelopes.ccLabel} {cc.cc.map(name)}</>}
    </p>
  );
}

/** A countdown in the row only this close to the opening; further out the date says more and does not tick. */
const COUNTDOWN_WITHIN_MS = 48 * 3600 * 1000;

/** The texts of the "opens …" column come with a leading " · " for the old one-line form; the column stands on its own. */
const bare = (s: string) => s.replace(/^\s*·\s*/, "");

export function CapsuleRow({
  view: listView,
  listing,
  selected,
  selecting,
  onSelect,
  includeOpened,
  onViewPerson,
  archived = false,
  archiveBusy = false,
  onArchive,
  onChangeView,
  focused = false,
  badges,
  note,
  extraActions,
}: {
  view: CapsuleView;
  listing: Listing;
  selected: boolean;
  selecting: boolean;
  /** Absent when there is nothing this member could open later (a private capsule sent without a copy). */
  onSelect?: () => void;
  includeOpened: boolean;
  onViewPerson?: (scope: MessageScope) => void;
  archived?: boolean;
  archiveBusy?: boolean;
  onArchive?: () => void;
  onChangeView?: (view: CapsuleView) => void;
  focused?: boolean;
  /** Correspondence (PLAN.md 4.21): tags after the "private" tag, such as "asks for a reply" or "stamp". */
  badges?: ReactNode;
  /** Correspondence: a line at the top of the detail strip, such as the envelope's terms. */
  note?: ReactNode;
  /** Correspondence: buttons in the actions bar, such as Reply. */
  extraActions?: ReactNode;
}) {
  const t = useT();
  const { me, memberByKey, myKeys, followed, nameOf, follows, watches, toggleFollow, toggleWatch, withKey, lockGeneration, lockStillValid, blocked, hidden, blockMember, hideMember } = useSession();
  const now = useNow(1000);
  // What this row opened itself (with the key, or a public capsule after its time), with the Lock
  // count it was opened under: it stays while the member is on the page, Lock hides it (owner,
  // 2026-10-07); leaving the page unmounts it.
  const [local, setLocal] = useState<{ view: CapsuleView; gen: number } | null>(null);
  useEffect(() => setLocal(null), [listView.id, listView.opened]);
  const view = local && local.gen === lockGeneration ? local.view : listView;
  const opened = view.opened;
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The body under the row (text or chest, intro, who and when): the chevron at the end of the
  // row shows it; cached messages start closed too. Actions live in the "⋯" menu.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (focused && opened) setExpanded(true); }, [focused, opened]);
  // Discreet sender (PLAN.md 4.16 K10): the name appears once the member clicks this capsule; Lock hides it again.
  const [senderShown, setSenderShown] = useState(false);
  useEffect(() => setSenderShown(false), [lockGeneration]);
  const senderHidden = view.senderHidden && !senderShown;
  // With every key of the account, so a capsule to an earlier key opens through the keyring (PLAN.md 4.18 step 6).
  const keyContext = { listing, memberId: me.id, myPubkey: me.pubkey, hideSenders: me.settings.hide_senders, myKeys };

  async function open() {
    setSenderShown(true);
    setOpening(true);
    setError(null);
    const locks = lockGeneration;
    try {
      let next: CapsuleView;
      if (!view.isPrivate) next = { ...view, opened: await openCapsule(view, me.id, null) };
      else {
        // Unlock on demand (4b step 2g): the key opens who and when, and the text once its time came.
        next = await withKey(t.keyDialog.purposes.open, async (key) => {
          const full = view.fromKey ? view : await revealWithKey(view, keyContext, key);
          const complete = { ...full, ...(view.recipients ? { recipients: view.recipients } : {}) };
          if (complete.opened || complete.unlockAt === null || complete.unlockAt > Date.now()) return complete;
          return { ...complete, opened: await openCapsule(complete, me.id, key) };
        });
      }
      // Locked while the beacon was on its way: keep nothing (it is in the encrypted cache).
      if (view.isPrivate && !lockStillValid(locks)) return;
      setLocal({ view: next, gen: locks });
      onChangeView?.(next);
      // The text, or while still time-locked what the key revealed, such as the intro (PLAN.md 4.17).
      setExpanded(true);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    } finally {
      setOpening(false);
    }
  }

  async function act(fn: () => Promise<void>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
  }

  /** One capsule's file; a private one goes through the key like the list download. */
  async function download() {
    const locks = lockGeneration;
    const base = { memberId: me.id, myKeys, includeOpened, stillUnlocked: () => lockStillValid(locks) };
    if (!view.isPrivate) return downloadCapsule(view, { ...base, secretKey: null });
    await withKey(t.keyDialog.purposes.download, async (key) => {
      const full = view.fromKey ? view : await revealWithKey(view, keyContext, key);
      await downloadCapsule(full, { ...base, secretKey: key });
    });
  }

  const sent = sentListing(listing);
  const who =
    sent
      ? view.isPrivate
        ? view.recipients && view.recipients.length > 1
          ? t.capsules.toMany(view.recipients.map((r) => nameOf(r)).join(", "))
          : t.capsules.to(nameOf(view.recipient))
        : t.capsules.toEveryone
      : <Author view={view} hidden={senderHidden} />;
  const locked = view.unlockAt !== null && view.unlockAt > now;
  // New (mail's unread): its time has passed and this account has not opened it on any device yet.
  const [markedRead, setMarkedRead] = useState(false);
  const isNew = !sent && view.unlockAt !== null && !locked && view.row.read === false && !markedRead;
  // Opened here (now, or earlier from this browser's cache): tell the server once, quietly.
  useEffect(() => {
    if (opened === null || view.row.read !== false || markedRead) return;
    setMarkedRead(true);
    api.markRead(view.id).then(() => onChangeView?.({ ...view, row: { ...view.row, read: true } }), () => undefined);
  }, [opened !== null, view.id, view.row.read]);

  if (view.damaged) return <DamagedRow reason={view.damaged} />;

  // Before the "⋯" and the chevron: the one thing to do with this capsule now, or why there is none.
  // An opened capsule needs no button: the chevron and the subject show its text.
  let state;
  if (view.unreadable) state = <span className="state muted">{view.unreadable}</span>;
  else if (view.needsKey)
    state = (
      <button className="primary" onClick={open} disabled={opening} title={t.capsules.privateText}>
        <Icon name="unlocked" size={15} />
        {opening ? t.common.opening : t.common.open}
      </button>
    );
  else if (opened !== null || locked) state = null;
  else
    state = (
      <button className="primary" onClick={open} disabled={opening}>
        <Icon name="eye" size={15} />
        {opening ? t.common.opening : t.common.open}
      </button>
    );

  const opensText =
    view.unlockAt !== null
      ? locked ? t.design.opensAt(formatTimeShort(view.unlockAt)) : t.design.availableSince(formatTimeShort(view.unlockAt))
      : view.recipient === me.pubkey
        ? t.capsules.opensWhenOpened
        : t.capsules.opensRecipientOnly;
  const showDetail = expanded || error !== null;
  // The intro is the only text readable before the opening: its first line previews the capsule in the list, as mail shows a snippet.
  const preview = !expanded && view.intro ? view.intro.split("\n").find((line) => line.trim())?.trim() ?? null : null;
  const authorKey = senderHidden || view.unverified ? null : view.author;
  const authorMember = memberByKey(authorKey);
  const authorName = senderHidden ? null : view.sender?.display_name ?? (authorKey === me.pubkey ? me.profile?.display_name ?? me.display_name : authorMember?.display_name);
  const avatar = !authorName ? "?" : initials(authorName);
  const canArchive = !!onArchive && (archived || view.unlockAt !== null && !locked);
  const otherAuthor = authorMember && authorName && authorMember.id !== me.id ? { id: authorMember.id, name: authorName } : null;
  const canHide = listing === "feed" && !!otherAuthor;
  const canBlock = (listing === "inbox" || listing === "envelopes_received") && !!otherAuthor;

  // The "⋯" menu: what else can be done with this capsule; absent when there is nothing.
  const hasActions = !!onSelect || !view.isPrivate || canHide || canBlock;
  const actions = (close: () => void) => {
    const run = (fn: () => Promise<void>) => { close(); void act(fn); };
    return <>
      {onSelect && (
        <button role="menuitem" onClick={() => run(download)} title={(downloadableEvent(view, myKeys)?.blobs.length ?? 0) > 0 ? t.capsules.downloadZip : t.capsules.downloadJson}>
          <Icon name="download" size={16} />{t.common.download}
        </button>
      )}
      {!view.isPrivate && (
        <button role="menuitem" onClick={() => run(() => toggleWatch(view.id))}>
          <Icon name="watching" size={16} />{watches.has(view.id) ? t.capsules.stopWatching : t.capsules.watch}
        </button>
      )}
      {!view.isPrivate && view.author && view.author !== me.pubkey && (
        <button role="menuitem" onClick={() => run(() => toggleFollow(view.author!))}>
          <Icon name="account" size={16} />{follows.has(view.author) ? t.capsules.unfollow(nameOf(view.author)) : t.capsules.follow(nameOf(view.author))}
        </button>
      )}
      {canHide && otherAuthor && (
        <button role="menuitem" onClick={() => run(() => hideMember(otherAuthor.id, !hidden.has(otherAuthor.id)))}>
          <Icon name="close" size={16} />{hidden.has(otherAuthor.id) ? t.design.unhide(otherAuthor.name) : t.design.hide(otherAuthor.name)}
        </button>
      )}
      {canBlock && otherAuthor && (
        <button role="menuitem" onClick={() => run(() => blockMember(otherAuthor.id, !blocked.has(otherAuthor.id)))}>
          <Icon name="lock" size={16} />{blocked.has(otherAuthor.id) ? t.design.unblock(otherAuthor.name) : t.design.block(otherAuthor.name)}
        </button>
      )}
    </>;
  };

  return (
    <li id={`capsule-${view.id}`} className={`capsule ${isNew ? "is-new" : ""} ${focused ? "search-hit" : ""} ${locked && opened === null ? "locked" : ""} ${opened !== null && expanded ? "is-reading" : ""}`}>
      <div className="row">
        {selecting && <span className="pick">{onSelect && <input type="checkbox" checked={selected} onChange={onSelect} aria-label={t.capsules.selectForDownload} />}</span>}
        <div className="row-head">
          <span className="who"><span className="sender-avatar" aria-hidden="true">{avatar}</span>{!sent && authorMember && authorName && onViewPerson ? <PersonMenu name={authorName} memberId={authorMember.id} onView={() => onViewPerson({ memberIds: [authorMember.id], label: authorName, generation: lockGeneration })} onWrite={() => navigate("/compose", { recipientIds: [authorMember.id] })} canWrite={!!authorMember.pubkey && (authorMember.id === me.id || followed.has(authorMember.id))}>{who}</PersonMenu> : <span>{who}</span>}</span>
          {(view.isPrivate || view.row.suspect || badges) && (
            <span className="row-tags">
              {view.isPrivate && <span className="tag">{t.capsules.private}</span>}
              {view.row.suspect && <span className="tag warn-tag" title={t.capsules.suspectNote}>{t.capsules.suspect}</span>}
              {badges}
            </span>
          )}
          <span className="when" title={`${creationTime(view) !== null ? t.capsules.written(formatTime(view.createdAt * 1000)) : t.browse.unknownCreated}${view.unlockAt !== null ? t.capsules.opensAt(formatTime(view.unlockAt)) : ""}`}>
            <span className="opening-date"><Icon name={locked ? "watching" : "check"} size={15} />{locked ? formatTimeShort(view.unlockAt!) : bare(opensText)}</span>
            {locked && view.unlockAt! - now < COUNTDOWN_WITHIN_MS && <span className="countdown">{t.design.countdown} <strong>{formatCountdownShort(view.unlockAt! - now)}</strong></span>}
          </span>
        </div>
        <div className="row-line">
          {isNew && <span className="new-dot" title={t.capsules.newLabel}><span className="visually-hidden">{t.capsules.newLabel}</span></span>}
          <button className="subject" dir="auto" onClick={() => { setSenderShown(true); if (opened !== null) setExpanded((x) => !x); else if (!locked && !view.unreadable) void open(); else setExpanded((x) => !x); }} disabled={opening}><span className="subject-text">{view.subject || t.design.untitled}</span>{preview && <span className="preview" dir="auto">{preview}</span>}</button>
          <span className="row-controls">
            {state}
            {canArchive && <button className="row-archive icon-button" disabled={archiveBusy} aria-label={archived ? t.browse.restore : t.browse.archive} title={archived ? t.browse.restore : t.browse.archive} onClick={onArchive}><Icon name={archived ? "restore" : "archive"} size={17} /></button>}
            {hasActions && <Menu label={t.design.messageActions} title={t.design.messageActions} className="more icon-button" align="end" trigger={<span aria-hidden="true">⋯</span>} items={actions} />}
            <button className="row-toggle icon-button" aria-expanded={expanded} aria-controls={`details-${view.id}`} aria-label={t.design.details} title={t.design.details} onClick={() => { setSenderShown(true); setExpanded((x) => !x); }}>
              <Icon name="down" size={18} />
            </button>
          </span>
        </div>
      </div>
      {showDetail && (
        <div className="detail" id={`details-${view.id}`}>
          {(note || extraActions) && <div className="envelope-head">{note}{extraActions && <div className="actions">{extraActions}</div>}</div>}
          {view.intro && <div className="intro"><span className="small muted">{t.capsules.intro}</span><p dir="auto">{view.intro}</p></div>}
          {locked && opened === null && (
            <div className="vault" role="timer" aria-live="off">
              <Chest open={false} />
              <div>
                <p className="vault-title">{t.design.sealedInside}</p>
                <p className="vault-when">{t.design.countdown}: <strong className="vault-timer">{formatCountdown(view.unlockAt! - now)}</strong> <span className="muted">({formatTimeShort(view.unlockAt!)})</span></p>
              </div>
            </div>
          )}
          {opened !== null && (
            <div className="vault vault-opened">
              <Chest open size={48} />
              {view.unlockAt !== null && <p className="small muted">{t.design.availableSince(formatTime(view.unlockAt))}</p>}
            </div>
          )}
          {opened !== null && opened.text && <div className="plaintext" dir="auto">{opened.text}</div>}
          {opened !== null && opened.files.length > 0 && <Attachments files={opened.files} />}
          {opened === null && view.needsKey && <p className="muted small">{t.capsules.privateText}</p>}
          {error && <p className="error small">{error}</p>}
          {expanded && (
            <div className="meta">
              <p className="small muted">{creationTime(view) !== null ? t.capsules.written(formatTime(view.createdAt * 1000)) : t.browse.unknownCreated}</p>
              {view.unlockAt !== null && !locked && opened === null && <p className="small muted">{t.design.availableSince(formatTime(view.unlockAt))}</p>}
              {!sent && <p className="small muted"><Author view={view} detailed hidden={senderHidden} /></p>}
              {view.cc && <CcLine cc={view.cc} />}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
